<template>
	<a
		:href="link"
		class="event-list-item"
		:class="{
			'-volley': isVolleyVariant,
			'-single': isSingleVariant,
			'-match': isMatchVariant,
		}"
	>
		<EventListItemTime
			class="event-list-item__time"
			:status="status"
			:variant="variant"
			:time="time"
			:competition-stage-info="competitionStageInfo"
		/>

		<EventListItemVolleyContent
			v-if="isVolleyVariant"
			class="event-list-item__title"
			:home-name="homeName"
			:home-name-full="homeNameFull"
			:home-img-url="homeImgUrl"
			:home-score-with-fallback="homeScoreWithFallback"
			:guest-name="guestName"
			:guest-name-full="guestNameFull"
			:guest-img-url="guestImgUrl"
			:guest-score-with-fallback="guestScoreWithFallback"
			:number-of-periods="numberOfPeriods"
			:scores="scores"
			:no-spoiler-mode="noSpoilerMode"
			:status="status"
		/>

		<EventListItemMatchContent
			v-else-if="isMatchVariant"
			class="event-list-item__title"
			:home-name="homeName"
			:home-name-full="homeNameFull"
			:home-img-url="homeImgUrl"
			:home-score-with-fallback="homeScoreWithFallback"
			:guest-name="guestName"
			:guest-name-full="guestNameFull"
			:guest-img-url="guestImgUrl"
			:guest-score-with-fallback="guestScoreWithFallback"
			:score-extra-abbr="scoreExtraAbbr"
			:no-spoiler-mode="noSpoilerMode"
			:dash-score-devider="dashScoreDevider"
		/>

		<EventListItemSingleContent
			v-else
			class="event-list-item__title"
			:title="title"
			:subtitle="subtitle"
			:sport-label="sportLabel"
			:discipline-label="disciplineLabel"
			:meta-items="metaItems"
			:participants="participants"
		/>

		<EventListItemActions
			class="event-list-item__action"
			:couch-mode-available="couchModeAvailable"
			:show-watch-label="isVolleyVariant"
			:variant="variant"
			@open-couch-mode="openCouchMode"
		/>
	</a>
</template>

<script>
import { getDateTime, getRelativeTime } from '@shared/utils/timeUtils';
import { getEventStatus, EVENT_STATUS } from '@shared/utils/eventUtils';
import { mapState } from 'vuex';
import EventListItemActions from './EventListItemActions.vue';
import EventListItemMatchContent from './EventListItemMatchContent.vue';
import EventListItemSingleContent from './EventListItemSingleContent.vue';
import EventListItemTime from './EventListItemTime.vue';
import EventListItemVolleyContent from './EventListItemVolleyContent.vue';

export default {
	name:       'EventListItem',
	components: {
		EventListItemActions,
		EventListItemMatchContent,
		EventListItemSingleContent,
		EventListItemTime,
		EventListItemVolleyContent,
	},
	props: {
		variant: {
			type:      String,
			required:  true,
			validator: value => ['volley', 'match', 'single'].includes(value),
		},
		title: {
			type:    String,
			default: null,
		},
		subtitle: {
			type:    String,
			default: null,
		},
		sportLabel: {
			type:    String,
			default: null,
		},
		disciplineLabel: {
			type:    String,
			default: null,
		},
		metaItems: {
			type:    Array,
			default: () => [],
		},
		participants: {
			type:    Array,
			default: () => [],
		},
		homeName: {
			type:    String,
			default: null,
		},
		homeNameFull: {
			type:    String,
			default: null,
		},
		homeImgUrl: {
			type:    String,
			default: null,
		},
		homeScore: {
			type:    String,
			default: null,
		},
		guestName: {
			type:    String,
			default: null,
		},
		guestNameFull: {
			type:    String,
			default: null,
		},
		guestImgUrl: {
			type:    String,
			default: null,
		},
		guestScore: {
			type:    String,
			default: null,
		},
		numberOfPeriods: {
			type:    Number,
			default: 0,
		},
		scores: {
			type:    Array,
			default: () => [],
		},
		scoreExtraAbbr: {
			type:    String,
			default: null,
		},
		link: {
			type:     String,
			required: true,
		},
		unixStart: {
			type:     Number,
			required: true,
		},
		unixKickoff: {
			type:     Number,
			required: true,
		},
		unixEnd: {
			type:     Number,
			required: true,
		},
		unixNow: {
			type:     Number,
			required: true,
		},
		eventType: {
			type:     String,
			required: true,
		},
		couchModeAvailable: {
			type:    Boolean,
			default: false,
		},
		competitionStageInfo: {
			type:    Object,
			default: null,
		},
		dashScoreDevider: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			EVENT_STATUS,
		};
	},
	computed: {
		...mapState({
			preferences: state => state.user.preferences,
		}),
		noSpoilerMode() {
			return this.preferences?.noSpoilerMode || false;
		},
		status() {
			return getEventStatus(this.eventType, this.unixStart, this.unixEnd, this.unixNow);
		},
		time() {
			switch (this.status) {
			case EVENT_STATUS.UPCOMING:
				return this.$I18n.trans('arena.match_center.live_from', { time: getDateTime(this.unixStart, { withDate: false }) });
			case EVENT_STATUS.LIVE:
				return getRelativeTime(this.unixEnd, { useRemaining: true, unixNow: this.unixNow });
			default:
				return getDateTime(this.unixKickoff, { withDate: false });
			}
		},
		homeScoreWithFallback() {
			return this.homeScore || (this.status === EVENT_STATUS.LIVE ? '0' : '');
		},
		guestScoreWithFallback() {
			return this.guestScore || (this.status === EVENT_STATUS.LIVE ? '0' : '');
		},
		isVolleyVariant() {
			return this.variant === 'volley';
		},
		isMatchVariant() {
			return this.variant === 'match';
		},
		isSingleVariant() {
			return this.variant === 'single';
		},
	},
	methods: {
		openCouchMode() {
			window.location.href = `${this.link}?autostartCouchmode`;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.event-list-item {
	position: relative;
	display: block;
	padding-top: $sp5;
	padding-bottom: $sp5;
	border-bottom: 1px solid var(--color-grey-900);

	&.-volley {
		padding-left: 0;
	}

	&:focus,
	&:hover {
		background-color: var(--color-grey-900);

		body.-t-light & { // stylelint-disable-line
			background-color: var(--color-grey-950);
		}
	}

	@include size(md-up) {
		display: flex;
		align-items: center;
		padding-top: 0;
		padding-bottom: 0;
		transition: background-color $trans-time-fast;

		&.-volley {
			height: 95px;
			padding-right: $sp4;
		}

		&.-single {
			height: 80px;
		}

		&.-match {
			height: 80px;
			justify-content: center;
		}
	}
}

.event-list-item__title {
	width: 100%;
}

.event-list-item__time {
	@include size(md-up) {
		position: absolute;
		left: $sp4;
		top: 0;
		height: 100%;
	}
}

.event-list-item__action {
	position: absolute;
	right: $sp4;
	top: 0;
	height: 100%;
}

.event-list-item:focus ::v-deep .event-list-item-actions__event-link-icon,
.event-list-item:hover ::v-deep .event-list-item-actions__event-link-icon {
	opacity: 1;
}

.event-list-item:focus ::v-deep .event-list-item-actions__couchmode-button::after,
.event-list-item:hover ::v-deep .event-list-item-actions__couchmode-button::after {
	opacity: 1;
}
</style>
